import { isEqual, memoize, orderBy } from "lodash";
import { apiCallGetPaidUsersInformation } from "../../../api/khattbat-api/api-call-get-paid-users-information";
import getCoreUIStore from "../../core/core-ui-store";
import getCorePopUpStore from "../../core/core-popup-store";
import { TGender, TPaidUsersInformation, TPicker2Option } from "../../../Types";
import { computed, observable, runInAction } from "mobx";
import {
  isDefinedArray,
  isDefinedString,
  isPositiveNumber,
  reduce
} from "et3-type-guards";
import { KHATTABAT_MATCHING_USER_INFORMATION_DEFAULT } from "../../../constants/user-profile";
import { apiCallGetMatchesIds } from "../../../api/khattbat-api/api-call-get-101M-matches";
import { apiCallAvailableCities } from "../../../api/khattbat-api/api-call-get-available-cities";
import getCoreHistoryStore from "../../core/core-history-store";
import {
  TEt3PickerMultipleValue,
  TEt3PickerSingleValue
} from "../../../TypesNEW";
import { reducerToStringArray } from "../../../helpers/et3-picker-data-helper";
import { DEFAULT_ET3_PICKER_SINGLE_VALUE } from "../../../constants/picker-entries-constants";
import getKhattabat101MGeneralStore from "../101m-stores/general-101m-store";
import { apiCallCreateOneOOneM } from "../../../api/khattbat-api/api-call-create-one-o-one-m";
import getKhattabatStore from "..";

class KhattabatMatchingStore {
  paidUsersInformation = observable.box<TPaidUsersInformation[]>([]);
  currentClientInformation = observable.box<TPaidUsersInformation>(
    KHATTABAT_MATCHING_USER_INFORMATION_DEFAULT
  );
  minMatchAgeFilter = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  maxMatchAgeFilter = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  minMatchWeightFilter = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  maxMatchWeightFilter = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  minMatchHeightFilter = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  maxMatchHeightFilter = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  matchNationalityFilter = observable.box<TEt3PickerMultipleValue>([]);
  matchResidenceFilter = observable.box<TEt3PickerMultipleValue>([]);
  matchResidenceCityFilter = observable.box<TEt3PickerMultipleValue>([]);
  availableCityOptions = observable.box<TPicker2Option[]>([
    {
      label: "",
      value: ""
    }
  ]);
  matchMarriageType = observable.box<TEt3PickerMultipleValue>([]);
  matchMaritalStatus = observable.box<TEt3PickerMultipleValue>([]);
  matchHavingKids = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  matchWork = observable.box<TEt3PickerMultipleValue>([]);
  matchEducation = observable.box<TEt3PickerMultipleValue>([]);
  matchFemaleWorkStudy = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  matchSmoking = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  matchSkinColor = observable.box<TEt3PickerMultipleValue>([]);
  matchFemaleVeil = observable.box<TEt3PickerMultipleValue>([]);
  matchMaleIncome = observable.box<TEt3PickerMultipleValue>([]);
  matchReligion = observable.box<TEt3PickerMultipleValue>([]);
  matchBelongsToATribe = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  matchRelocation = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  matchAppLanguage = observable.box<TEt3PickerSingleValue>({
    label: "Arabic",
    value: "AR"
  });
  matchesIdsResultLimit = observable.box<TEt3PickerSingleValue>({
    label: "10",
    value: "10"
  });
  isChosenNationalitiesExcluded = observable.box<boolean>(false);
  isChosenResidenceCountriesExcluded = observable.box<boolean>(false);
  matchesIds = observable.box<number[]>([]);
  selectedMatchesIds = observable.box<number[]>([]);

  setPaidUsersInformation = (paidUsersInformation: TPaidUsersInformation[]) => {
    const formatedPaidUsersInformation = orderBy(
      paidUsersInformation,
      "userStatus",
      "asc"
    );
    runInAction(() => {
      this.paidUsersInformation.set(formatedPaidUsersInformation);
    });
  };

  isNationalitiesExcludingDisabled = computed(() => {
    return !isDefinedArray(
      reducerToStringArray(this.matchNationalityFilter.get())
    );
  });

  isResidenceExcludingDisabled = computed(() => {
    return !isDefinedArray(
      reducerToStringArray(this.matchResidenceFilter.get())
    );
  });

  setCurrentUserInformation = (userIDAndName: TEt3PickerSingleValue) => {
    const arrayOfInput = userIDAndName.value.split(",");
    const userID = parseInt(arrayOfInput[0], 10);
    const information = this.getCurrentUserInformationByUserID(userID);
    runInAction(() => {
      this.currentClientInformation.set(information);
    });
  };

  isResettingMatchFilter = (value: TEt3PickerSingleValue) => {
    return isEqual(value, DEFAULT_ET3_PICKER_SINGLE_VALUE);
  };

  resetMatchAgeFilters = () => {
    this.setMaxMatchAgeFilter(DEFAULT_ET3_PICKER_SINGLE_VALUE);
    this.setMinMatchAgeFilter(DEFAULT_ET3_PICKER_SINGLE_VALUE);
  };

  setMinMatchAgeFilter = (age: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.minMatchAgeFilter.set(age);

      if (this.isResettingMatchFilter(age)) {
        return;
      }
      const maxAge = this.maxMatchAgeFilter.get();
      if (maxAge.value !== "" && age.value > maxAge.value) {
        getCorePopUpStore().showAlertMessage("MIN_AGE_LESS_THAN_MAX_AGE");
        this.setMaxMatchAgeFilter(DEFAULT_ET3_PICKER_SINGLE_VALUE);
      }
    });
  };

  setMaxMatchAgeFilter = (age: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.maxMatchAgeFilter.set(age);

      if (this.isResettingMatchFilter(age)) {
        return;
      }
      const minAge = this.minMatchAgeFilter.get();
      if (minAge.value !== "" && minAge.value > age.value) {
        getCorePopUpStore().showAlertMessage("MIN_AGE_LESS_THAN_MAX_AGE");
        this.setMinMatchAgeFilter(DEFAULT_ET3_PICKER_SINGLE_VALUE);
      }
    });
  };

  resetMatchWeightFilters = () => {
    this.setMaxMatchWeightFilter(DEFAULT_ET3_PICKER_SINGLE_VALUE);
    this.setMinMatchWeightFilter(DEFAULT_ET3_PICKER_SINGLE_VALUE);
  };

  setMinMatchWeightFilter = (weight: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.minMatchWeightFilter.set(weight);

      if (this.isResettingMatchFilter(weight)) {
        return;
      }
      const maxWeight = this.maxMatchWeightFilter.get();
      const maxWeightValue = parseInt(maxWeight.value, 10);
      const selectedWeightValue = parseInt(weight.value, 10);

      if (maxWeight.value !== "" && selectedWeightValue > maxWeightValue) {
        getCorePopUpStore().showAlertMessage("MIN_WEIGHT_LESS_THAN_MAX_WEIGHT");
        this.setMaxMatchWeightFilter(DEFAULT_ET3_PICKER_SINGLE_VALUE);
      }
    });
  };

  setMaxMatchWeightFilter = (weight: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.maxMatchWeightFilter.set(weight);

      if (this.isResettingMatchFilter(weight)) {
        return;
      }
      const minWeight = this.minMatchWeightFilter.get();
      const minWeightValue = parseInt(minWeight.value, 10);
      const selectedWeightValue = parseInt(weight.value, 10);

      if (minWeight.value !== "" && minWeightValue > selectedWeightValue) {
        getCorePopUpStore().showAlertMessage("MIN_WEIGHT_LESS_THAN_MAX_WEIGHT");
        this.setMinMatchWeightFilter(DEFAULT_ET3_PICKER_SINGLE_VALUE);
      }
    });
  };

  resetMatchHeightFilters = () => {
    this.setMaxMatchHeightFilter(DEFAULT_ET3_PICKER_SINGLE_VALUE);
    this.setMinMatchHeightFilter(DEFAULT_ET3_PICKER_SINGLE_VALUE);
  };

  setMinMatchHeightFilter = (height: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.minMatchHeightFilter.set(height);

      if (this.isResettingMatchFilter(height)) {
        return;
      }
      const maxHeight = this.maxMatchHeightFilter.get();
      const maxHeightValue = parseInt(maxHeight.value, 10);
      const selectedHeightValue = parseInt(height.value, 10);

      if (maxHeight.value !== "" && selectedHeightValue > maxHeightValue) {
        getCorePopUpStore().showAlertMessage("MIN_HEIGHT_LESS_THAN_MAX_HEIGHT");
        this.setMaxMatchHeightFilter(DEFAULT_ET3_PICKER_SINGLE_VALUE);
      }
    });
  };

  setMaxMatchHeightFilter = (height: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.maxMatchHeightFilter.set(height);

      if (this.isResettingMatchFilter(height)) {
        return;
      }
      const minHeight = this.minMatchHeightFilter.get();
      const minHeightValue = parseInt(minHeight.value, 10);
      const selectedHeightValue = parseInt(height.value, 10);

      if (minHeight.value !== "" && minHeightValue > selectedHeightValue) {
        getCorePopUpStore().showAlertMessage("MIN_HEIGHT_LESS_THAN_MAX_HEIGHT");
        this.setMinMatchHeightFilter(DEFAULT_ET3_PICKER_SINGLE_VALUE);
      }
    });
  };

  setMatchNationalityFilter = (nationality: TEt3PickerMultipleValue) => {
    runInAction(() => {
      this.matchNationalityFilter.set(nationality);
    });
  };

  setMatchResidenceFilter = (residence: TEt3PickerMultipleValue) => {
    this.resetCitySearch();
    runInAction(() => {
      this.matchResidenceFilter.set(residence);
    });
  };

  setMatchResidenceCityFilter = (city: TEt3PickerMultipleValue) => {
    runInAction(() => {
      this.matchResidenceCityFilter.set(city);
    });
  };

  setAvailableCityOptions = (cities: TPicker2Option[]) => {
    runInAction(() => {
      this.availableCityOptions.set(cities);
    });
  };

  setMatchMaritalStatus = (maritalStatus: TEt3PickerMultipleValue) => {
    runInAction(() => {
      this.matchMaritalStatus.set(maritalStatus);
    });
  };
  setMatchHavingKids = (havingKids: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.matchHavingKids.set(havingKids);
    });
  };

  setMatchWork = (matchWork: TEt3PickerMultipleValue) => {
    runInAction(() => {
      this.matchWork.set(matchWork);
    });
  };
  setMatchEducation = (matchEducation: TEt3PickerMultipleValue) => {
    runInAction(() => {
      this.matchEducation.set(matchEducation);
    });
  };
  setMatchFemaleWorkStudy = (matchFemaleWorkStudy: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.matchFemaleWorkStudy.set(matchFemaleWorkStudy);
    });
  };

  setMatchSmoking = (matchSmoking: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.matchSmoking.set(matchSmoking);
    });
  };

  setMatchSkinColor = (matchSkinColor: TEt3PickerMultipleValue) => {
    runInAction(() => {
      this.matchSkinColor.set(matchSkinColor);
    });
  };

  setMatchFemaleVeil = (matchFemaleVeil: TEt3PickerMultipleValue) => {
    runInAction(() => {
      this.matchFemaleVeil.set(matchFemaleVeil);
    });
  };
  setMatchMaleIncome = (matchMaleIncome: TEt3PickerMultipleValue) => {
    runInAction(() => {
      this.matchMaleIncome.set(matchMaleIncome);
    });
  };

  setMatchReligion = (matchReligion: TEt3PickerMultipleValue) => {
    runInAction(() => {
      this.matchReligion.set(matchReligion);
    });
  };

  setMatchBelongsToATribe = (matchBelongsToATribe: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.matchBelongsToATribe.set(matchBelongsToATribe);
    });
  };
  setMatchRelocation = (matchRelocation: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.matchRelocation.set(matchRelocation);
    });
  };

  setMatchAppLanguage = (languageValue: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.matchAppLanguage.set(languageValue);
    });
  };

  setMatchesIdsResultLimit = (limit: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.matchesIdsResultLimit.set(limit);
    });
  };

  setIsChosenNationalitiesExcluded = (value: boolean) => {
    runInAction(() => {
      this.isChosenNationalitiesExcluded.set(value);
    });
  };

  setIsChosenResidenceCountriesExcluded = (value: boolean) => {
    runInAction(() => {
      this.isChosenResidenceCountriesExcluded.set(value);
    });
  };

  toggleIsChosenNationalitiesExcluded = () => {
    runInAction(() => {
      this.setIsChosenNationalitiesExcluded(
        !this.isChosenNationalitiesExcluded.get()
      );
    });
  };

  toggleIsChosenResidenceCountriesExcluded = () => {
    this.resetCitySearch();
    runInAction(() => {
      this.setIsChosenResidenceCountriesExcluded(
        !this.isChosenResidenceCountriesExcluded.get()
      );
    });
  };

  setMatchesIds = (matchesIds: number[]) => {
    runInAction(() => {
      this.matchesIds.set(matchesIds);
    });
  };
  setSelectedMatchesIds = (selectedMatchesIds: number[]) => {
    runInAction(() => {
      this.selectedMatchesIds.set(selectedMatchesIds);
    });
  };

  setDefaultSelectedMatchesIds = () => {
    this.setSelectedMatchesIds(this.matchesIds.get());
  };

  clearSelectedMatchesIds = () => {
    this.setSelectedMatchesIds([]);
  };

  isMatchUserSelected = (userID: number) => {
    return this.selectedMatchesIds.get().includes(userID);
  };

  toggleSelectedMatchUser = (userID: number) => {
    const selectedMatchesIds = this.selectedMatchesIds.get();

    if (selectedMatchesIds.includes(userID)) {
      this.setSelectedMatchesIds(
        selectedMatchesIds.filter((selectedUserID) => {
          return selectedUserID !== userID;
        })
      );
      return;
    }

    this.setSelectedMatchesIds([...selectedMatchesIds, userID]);
  };

  selectedMatchesCount = computed(() => {
    return this.selectedMatchesIds.get().length;
  });

  isSendSelectedMatchesDisabled = computed(() => {
    return !isDefinedArray(this.selectedMatchesIds.get());
  });

  getUsersIdsOptions = (returnCount: boolean) => {
    const initialValue = { label: "", value: "", badge: 0 };
    if (!isDefinedArray(this.paidUsersInformation.get())) {
      return [initialValue];
    }
    const data = this.paidUsersInformation.get().map((information) => {
      return {
        label: `${information.userId}, ${information.userName}`,
        value: `${information.userId}, ${information.userName}`,
        badge: returnCount && information.totalUnreadMessages
      };
    });
    data.unshift(initialValue);
    return data;
  };

  getCurrentPaidUserPickerValue = () => {
    const currentClientInformation = this.currentClientInformation.get();

    if (!isPositiveNumber(currentClientInformation.userId)) {
      return {
        label: "",
        value: ""
      };
    }

    const userInformation = `${currentClientInformation.userId}, ${currentClientInformation.userName}`;
    return {
      label: userInformation,
      value: userInformation
    };
  };

  getCurrentUserInformationByUserID = (userID: number) => {
    const arrayOfOneUserInformation = this.paidUsersInformation
      .get()
      .filter((information) => {
        return information.userId === userID;
      });
    const reducedValue = reduce(
      () => arrayOfOneUserInformation[0],
      KHATTABAT_MATCHING_USER_INFORMATION_DEFAULT
    );
    return reducedValue;
  };

  getPaidUsersInformation = async () => {
    const khattabatMatchingPage = getCoreHistoryStore().getPageName();
    getCoreUIStore().setIsLoading(khattabatMatchingPage, true);
    const { status, code, data } = await apiCallGetPaidUsersInformation();
    getCoreUIStore().setIsLoading(khattabatMatchingPage, false);
    if (status === "OK") {
      this.setPaidUsersInformation(data);
      getKhattabatStore().setAlkhattabaUUID(
        reduce(() => data[0].alkhatabaUUID, "")
      );
      getKhattabat101MGeneralStore().resetSelectAll();
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  getMatchGender = (): TGender => {
    const currentClientGender = this.currentClientInformation.get().gender;
    switch (currentClientGender) {
      case "f":
        return "m";
      case "m":
        return "f";
      default:
        return "";
    }
  };

  setMarriageState = (marriageType: TEt3PickerMultipleValue) => {
    runInAction(() => {
      this.matchMarriageType.set(marriageType);
    });
  };
  resetCitySearch = () => {
    this.setMatchResidenceCityFilter([]);
    this.setAvailableCityOptions([DEFAULT_ET3_PICKER_SINGLE_VALUE]);
  };

  resetFilter = () => {
    this.resetMatchAgeFilters();
    this.resetMatchHeightFilters();
    this.resetMatchWeightFilters();
    this.setMatchNationalityFilter([]);
    this.setMatchResidenceFilter([]);
    this.resetCitySearch();
    this.setMarriageState([]);
    this.setMatchMaritalStatus([]);
    this.setMatchHavingKids(DEFAULT_ET3_PICKER_SINGLE_VALUE);
    this.setMatchWork([]);
    this.setMatchEducation([]);
    this.setMatchFemaleWorkStudy(DEFAULT_ET3_PICKER_SINGLE_VALUE);
    this.setMatchSmoking(DEFAULT_ET3_PICKER_SINGLE_VALUE);
    this.setMatchBelongsToATribe(DEFAULT_ET3_PICKER_SINGLE_VALUE);
    this.setMatchRelocation(DEFAULT_ET3_PICKER_SINGLE_VALUE);
    this.setMatchesIdsResultLimit({ label: "10", value: "10" });
    this.setMatchesIds([]);
    this.setMatchSkinColor([]);
    this.setMatchReligion([]);
    this.setMatchFemaleVeil([]);
    this.setMatchMaleIncome([]);
    this.setMatchesIds([]);
    this.clearSelectedMatchesIds();
  };

  isActiveFilter = computed(() => {
    return (
      isPositiveNumber(parseInt(this.minMatchAgeFilter.get().value, 10)) ||
      isPositiveNumber(parseInt(this.maxMatchAgeFilter.get().value, 10)) ||
      isPositiveNumber(parseInt(this.minMatchWeightFilter.get().value, 10)) ||
      isPositiveNumber(parseInt(this.maxMatchWeightFilter.get().value, 10)) ||
      isPositiveNumber(parseInt(this.minMatchHeightFilter.get().value, 10)) ||
      isPositiveNumber(parseInt(this.maxMatchHeightFilter.get().value, 10)) ||
      isDefinedArray(this.matchNationalityFilter.get()) ||
      isDefinedArray(this.matchResidenceFilter.get()) ||
      isDefinedArray(this.matchMarriageType.get()) ||
      isDefinedArray(this.matchMaritalStatus.get()) ||
      isDefinedString(this.matchHavingKids.get().value.trim()) ||
      isDefinedArray(this.matchWork.get()) ||
      isDefinedArray(this.matchEducation.get()) ||
      isDefinedString(this.matchFemaleWorkStudy.get().value.trim()) ||
      isDefinedString(this.matchSmoking.get().value.trim()) ||
      isDefinedString(this.matchBelongsToATribe.get().value.trim()) ||
      isDefinedString(this.matchRelocation.get().value.trim()) ||
      isDefinedArray(this.matchSkinColor.get()) ||
      isDefinedArray(this.matchReligion.get()) ||
      (isDefinedArray(this.matchFemaleVeil.get()) &&
        this.isMatchingVeilCriteriaExists.get()) ||
      (isDefinedArray(this.matchMaleIncome.get()) &&
        this.isMatchingIncomeCriteriaExists.get())
    );
  });

  isMatchingVeilCriteriaExists = computed(() => {
    const currentPaidClientGender = this.currentClientInformation.get().gender;
    return currentPaidClientGender === "m";
  });
  isMatchingIncomeCriteriaExists = computed(() => {
    const currentPaidClientGender = this.currentClientInformation.get().gender;
    return currentPaidClientGender === "f";
  });

  isCurrentUserExist = () => {
    return isPositiveNumber(this.currentClientInformation.get().userId);
  };

  canGetMatches = () => {
    return this.isActiveFilter.get() && this.isCurrentUserExist();
  };

  handleAgeRangeFilter = () => {
    const isPositiveMaxAgeFilter = isPositiveNumber(
      this.maxMatchAgeFilter.get()
    );
    const isPositiveMinAgeFilter = isPositiveNumber(
      this.minMatchAgeFilter.get()
    );
    const wrongCompare =
      this.maxMatchAgeFilter.get() < this.minMatchAgeFilter.get();
    const bothPositive = isPositiveMaxAgeFilter && isPositiveMinAgeFilter;
    if (wrongCompare && bothPositive) {
      getCorePopUpStore().showAlertMessage("MAX_AGE_HAS_TO_BE_BIGGER_MIN_AGE");
      const currentMinAgeValue = this.minMatchAgeFilter.get();
      const currentMaxAgeValue = this.maxMatchAgeFilter.get();
      this.setMinMatchAgeFilter(currentMaxAgeValue);
      this.setMaxMatchAgeFilter(currentMinAgeValue);
    }
  };

  getMatchesIds = async () => {
    this.handleAgeRangeFilter();
    getCoreUIStore().setIsLoading("KHATTABAT_MATCHING_SEARCH_AND_1O1M", true);

    this.setMatchesIds([]);
    const { status, code, data } = await apiCallGetMatchesIds();
    getCoreUIStore().setIsLoading("KHATTABAT_MATCHING_SEARCH_AND_1O1M", false);
    if (status === "OK") {
      this.setMatchesIds(data);
      this.setSelectedMatchesIds(data);
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  isCitySearchAvailable = computed(() => {
    return isDefinedArray(this.matchResidenceFilter.get());
  });

  getAvailableCities = async () => {
    getCoreUIStore().setIsLoading("KHATTABAT_MATCHING_SEARCH_AND_1O1M", true);
    const { status, code, data } = await apiCallAvailableCities({
      residenceCountryCodes: reducerToStringArray(
        this.matchResidenceFilter.get()
      )
    });
    getCoreUIStore().setIsLoading("KHATTABAT_MATCHING_SEARCH_AND_1O1M", false);
    if (status === "OK") {
      const formatedOptions = data.map((city) => {
        return {
          label: city,
          value: city
        };
      });
      formatedOptions.unshift(DEFAULT_ET3_PICKER_SINGLE_VALUE);
      this.setAvailableCityOptions(formatedOptions);
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  openOneOOneMConversations = async () => {
    const clientID = this.currentClientInformation.get().userId;
    const matchIDs = this.selectedMatchesIds.get();
    const message =
      getKhattabat101MGeneralStore().messageToSendToCreated101M.get();

    getCoreUIStore().setIsLoading("KHATTABAT_MATCHING_SEARCH_AND_1O1M", true);
    try {
      const { status, code, data } = await apiCallCreateOneOOneM({
        clientID,
        matchIDs,
        message
      });

      if (status === "OK") {
        const parsedData = data
          .map(({ matchID, status }) => `${matchID}_${status}`)
          .join("\n");
        getCorePopUpStore().showSuccessMessageWithExtraData(
          "ONE_O_ONE_M_SENT_RESULT",
          parsedData,
          true
        );
        getKhattabat101MGeneralStore().toggleIsMatchesIdsSelectedAll();
        this.resetFilter();
      } else {
        getCorePopUpStore().showAlertMessage(code);
      }
    } catch (error) {
      getCorePopUpStore().showAlertMessage(error);
      return error;
    } finally {
      getCoreUIStore().setIsLoading(
        "KHATTABAT_MATCHING_SEARCH_AND_1O1M",
        false
      );
    }
  };
}

export const getKhattabatMatchingStore = memoize(
  () => {
    return new KhattabatMatchingStore();
  },
  () => 1
);

export default getKhattabatMatchingStore;
